import React from 'react';
import {StyleSheet, View} from 'react-native';

import {H3Text, H5Text} from '../../components/core/text';
import {useColorThemeType} from '../../appStyles/hooks/theme';
import {ClaimStatus} from '../types/shiftClaim';

interface ShiftClaimErrorProps {
  status: ClaimStatus;
}

interface ErrorCopy {
  emoji: string;
  title: string;
  body: string;
}

const ERROR_COPY: Partial<Record<ClaimStatus, ErrorCopy>> = {
  [ClaimStatus.Expired]: {
    emoji: '⏳',
    title: 'Link Expired',
    body: 'This shift claim link has expired. Please contact your team for a new link.',
  },
  [ClaimStatus.Invalid]: {
    emoji: '🔗',
    title: 'Invalid Link',
    body: 'This link is not valid. Please check the SMS message and try again.',
  },
  [ClaimStatus.AlreadyClaimed]: {
    emoji: '✅',
    title: 'Shift Already Claimed',
    body: 'This shift has already been claimed. Please check with your team for available shifts.',
  },
  [ClaimStatus.NetworkError]: {
    emoji: '📡',
    title: 'Connection Error',
    body: 'Unable to load shift details. Please check your internet connection and refresh the page.',
  },
};

const FALLBACK: ErrorCopy = ERROR_COPY[ClaimStatus.Invalid]!;

const ShiftClaimError: React.FC<ShiftClaimErrorProps> = ({status}) => {
  const theme = useColorThemeType('main');
  const copy = ERROR_COPY[status] ?? FALLBACK;

  return (
    <View style={styles.container}>
      <H3Text style={styles.emoji}>{copy.emoji}</H3Text>
      <H3Text style={[styles.title, {color: theme?.text.blue[300]}]}>
        {copy.title}
      </H3Text>
      <H5Text style={[styles.body, {color: theme?.text.gray[100]}]}>
        {copy.body}
      </H5Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    maxWidth: 480,
    alignSelf: 'center',
    width: '100%',
  },
  emoji: {
    fontSize: 52,
    marginBottom: 20,
    textAlign: 'center',
  },
  title: {
    marginBottom: 12,
    textAlign: 'center',
  },
  body: {
    textAlign: 'center',
    lineHeight: 24,
  },
});

export default React.memo(ShiftClaimError);
